import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal, Spin, Tooltip } from 'antd';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import {
  ReactComponent as CrossSvg,
  ReactComponent as CloseSvg
} from '@assets/icons/cross.svg';
import { ReactComponent as InfoSvg } from '@assets/icons/info-icon.svg';
import { ReactComponent as ContributorsLimitErrorSvg } from '@assets/icons/contributors_limit_error_notification.svg';
import { useTypedSelector } from '@hooks';
import { AssetItemDto } from '@api/Api';
import {
  toggleApprovalConflictAssetsModal,
  toggleApprovalModal
} from '@redux/actions/modalAction';
import { useAuth } from '@hooks/useAuth';
import { useCurrentWorkspace } from '@hooks/workspace';
import { ApprovalConflictAssetsModalStateProps } from '@redux/types/modalType';
import AssetCard from '@components/Modals/ApprovalConflictAssetsModal/components/AssetCard';
import { useAssetsQuery } from '@hooks/queries/assets';

import './ApprovalConflictAssetsModal.less';

const statusPriority = {
  pending: 0,
  in_progress: 0,
  paused: 1,
  skipped: 2,
  changes_required: 3,
  approved_with_feedback: 4,
  approved: 5
};

function ApprovalConflictAssetsModal({
  visible,
  workspaceId,
  campaignId,
  title,
  parentId,
  callback
}: ApprovalConflictAssetsModalStateProps) {
  const dispatch = useDispatch();
  const [currentWorkspace] = useCurrentWorkspace(false);
  const [unlinkApprovalAssets, setUnlinkApprovalAssets] = useState<
    AssetItemDto[]
  >([]);

  const canCreateAw = !!currentWorkspace?.permissions.createApprovalWorkflows;

  const {
    data,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isLoading,
    isRefetching
  } = useAssetsQuery(
    {
      workspaceId: workspaceId ?? '',
      campaignId,
      parentId,
      isFolder: false,
      flatten: true,
      limit: 100
    },
    { enabled: visible && !!workspaceId }
  );

  const localAssets = useMemo(() => {
    return (
      data?.pages.flatMap((page) =>
        page.edges
          .filter((edge) => !edge.node.isFolder)
          .map((edge) => edge.node)
      ) ?? []
    );
  }, [data]);

  const loading = isLoading || isFetchingNextPage || isRefetching;

  useEffect(() => {
    if (isFetchingNextPage || !hasNextPage) return;
    fetchNextPage();
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  const assetsWithWorkflow = useMemo(() => {
    if (!localAssets.length) return [];
    return localAssets
      .filter((item) => !!item?.asset?.approvalWorkflow)
      .sort((a, b) => {
        const aStatus = a.asset?.approvalWorkflow?.isPaused
          ? 'paused'
          : a.asset?.approvalWorkflow?.status || 'paused';
        const bStatus = b.asset?.approvalWorkflow?.isPaused
          ? 'paused'
          : b.asset?.approvalWorkflow?.status || 'paused';
        return statusPriority[aStatus] - statusPriority[bStatus];
      });
  }, [localAssets]);

  const onCloseModal = useCallback(
    () => dispatch(toggleApprovalConflictAssetsModal(null)),
    [dispatch]
  );

  const hasAllAssetsWithAW = useMemo(() => {
    return localAssets.every((item) => !!item?.asset?.approvalWorkflow);
  }, [localAssets]);

  const isDisabledBtn = hasAllAssetsWithAW && !unlinkApprovalAssets.length;

  const confirmRemoveApproval = () => {
    const confirmRemoveModal = Modal.confirm({
      onCancel: () => {
        if (hasAllAssetsWithAW) {
          confirmRemoveModal.destroy();
          return;
        }
        onCreateApproval(false);
      },
      onOk: () => onCreateApproval(true),
      width: 545,
      icon: null,
      centered: true,
      okText: 'Yes & Create approval routing',
      cancelText: hasAllAssetsWithAW ? 'Cancel' : 'No & Keep current',
      closable: true,
      maskClosable: true,
      closeIcon: <CloseSvg />,
      zIndex: 1010,
      content: (
        <div className="modal_container">
          <h2
            className="modal_container__title"
            style={{ fontSize: 22, marginBottom: '0.5em' }}
          >
            Are you sure you want to remove the approval for the selected media?
          </h2>
          <p className="modal_container__subtitle">
            All related approval stages and data will be permanently deleted.
          </p>
        </div>
      )
    });
  };

  const onCreateApproval = async (isDeleteAwInAssets = false) => {
    if (!campaignId) return;
    let assets = localAssets.filter((item) => !item?.asset?.approvalWorkflow);

    if (isDeleteAwInAssets) assets = [...assets, ...unlinkApprovalAssets];

    const assetVersions = assets
      .filter((item) => !!item.asset?.versions[0])
      .map((item) => ({
        ...item.asset!.versions[0],
        assetId: item.id
      }));
    onCloseModal();
    dispatch(
      toggleApprovalModal({
        visible: true,
        assetVersions,
        permissions: {
          create: canCreateAw
        },
        campaignId,
        folderId: parentId,
        callback: (aw) => {
          callback?.(aw);
        }
      })
    );
  };

  const onToggleSelect = (asset: AssetItemDto, isSelected: boolean) => {
    if (isSelected) {
      setUnlinkApprovalAssets((prevUnlinkApprovalAssets) =>
        prevUnlinkApprovalAssets.filter((item) => item.id !== asset.id)
      );
      return;
    }
    setUnlinkApprovalAssets((prevUnlinkApprovalAssets) => [
      ...prevUnlinkApprovalAssets,
      asset
    ]);
  };

  useEffect(() => {
    if (loading) return;
    if (hasNextPage) return;
    if (assetsWithWorkflow.length) return;
    onCreateApproval();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasNextPage, assetsWithWorkflow]);

  if (!loading && !localAssets.length) {
    return (
      <Modal
        open={visible}
        footer={null}
        centered={true}
        width={512}
        destroyOnClose
        zIndex={1008}
        onCancel={onCloseModal}
        className={classNames('approval-conflict-assets')}
        afterClose={() => dispatch(toggleApprovalModal(null))}
      >
        <CrossSvg
          className="approval-conflict-assets_close-btn"
          onClick={onCloseModal}
        />
        <div className="approval-conflict-assets_empty-head">
          <ContributorsLimitErrorSvg />
          <div>Unable to create approval</div>
        </div>
        <div className="approval-conflict-assets_empty-text">
          You need to add at least one media before creating an approval. Please
          upload a media to proceed.
        </div>
        <div className="approval-conflict-assets_bottom">
          <Button
            type="primary"
            onClick={onCloseModal}
            style={{ minWidth: 152 }}
          >
            Ok
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={visible}
      footer={null}
      centered={true}
      width={800}
      destroyOnClose
      zIndex={1008}
      onCancel={onCloseModal}
      className={classNames('approval-conflict-assets')}
      afterClose={() => dispatch(toggleApprovalModal(null))}
    >
      {loading && !assetsWithWorkflow.length && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh'
          }}
        >
          <Spin size="large" />
        </div>
      )}
      {!loading && !!assetsWithWorkflow.length && (
        <>
          <CrossSvg
            className="approval-conflict-assets_close-btn"
            onClick={onCloseModal}
          />
          <div className="approval-conflict-assets_wrapper">
            <div className="approval-conflict-assets_title">
              {title || 'Approval routing'}
            </div>
            <div className="approval-conflict-assets_info">
              <InfoSvg className="approval-conflict-assets_info-icon" />
              <div className="approval-conflict-assets_info-text">
                These media are in an active approval routing. To add them to a
                new approval routing, you&apos;ll need to first remove them from
                the current one.
              </div>
            </div>
            <OverlayScrollbarsComponent className="approval-conflict-assets_content">
              <div className="approval-conflict-assets_content-inner">
                {assetsWithWorkflow.map((asset) => {
                  const isSelected = unlinkApprovalAssets.some(
                    (item) => item.id === asset.id
                  );
                  return (
                    <div
                      key={asset.id}
                      className="approval-conflict-assets_item"
                    >
                      <AssetCard
                        asset={asset}
                        isSelected={!!isSelected}
                        onToggleSelect={onToggleSelect}
                      />
                    </div>
                  );
                })}
              </div>
            </OverlayScrollbarsComponent>
            <div className="approval-conflict-assets_bottom">
              <Button type="text" onClick={onCloseModal}>
                Cancel
              </Button>
              <Tooltip
                title="All assets are currently in an active approval workflow"
                overlayClassName="toggle_favorite_overlay"
                trigger={isDisabledBtn ? ['hover', 'click'] : []}
              >
                <Button
                  type="primary"
                  disabled={isDisabledBtn}
                  onClick={() =>
                    unlinkApprovalAssets.length
                      ? confirmRemoveApproval()
                      : onCreateApproval()
                  }
                >
                  Skip & Create approval routing
                </Button>
              </Tooltip>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}

export default function ApprovalConflictAssetsModalContainer() {
  const { user } = useAuth();
  const modalState = useTypedSelector(
    ({ modal }) => modal.approvalConflictAssetsModal
  );
  if (!modalState || !user) return null;
  return <ApprovalConflictAssetsModal {...modalState} />;
}
